import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { GasInputBoolean } from '@cikrf/gas-utils/decorators';
import { skip } from 'rxjs/operators';
import { GasTable } from '../gas-table/gas-table.namespace';
import { GasPaginator } from './gas-paginator.namespace';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/** @see https://www.figma.com/file/yqBeq1MiGfpdN62SvmA8tg/RTLABS_GAS_DS_1?node-id=1792%3A23560 */
/** @description Компонент для отображения контента в страничном формате */
@UntilDestroy()
@Component({
  selector: 'gas-paginator',
  templateUrl: './gas-paginator.component.html',
  styleUrls: ['./gas-paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasPaginatorComponent implements OnInit, OnChanges {

  /** Количество элементов отображаемых на странице */
  @Input()
  public perPage = 10;

  /** Селектор для выбора количества отображаемых элементов */
  @Input()
  public perPageList: number[] = [5, 10, 20, 50];

  /** Общее количество элементов на всех страницах */
  @Input()
  public total = 1;

  /** Количество отображаемых страниц */
  @Input()
  public limit = 9;

  /** Номер текущей страницы */
  @Input()
  public page = 1;

  @GasInputBoolean()
  @Input()
  public hideDetails: string | boolean = false;

  @Input()
  public viewData: GasTable.TableValue[] = [];

  @Output('changed')
  public changed$ = new EventEmitter<GasPaginator.Data>();

  public pages: number[] = [];

  public paginatorData: GasPaginator.Data = null;

  public perPageControl = new FormControl();

  public get from(): string {
    const page = this.page;

    if (this.viewData.length === 0) {
      return String(0);
    }

    return String(page > 1
      ? ((page - 1) * this.perPage) + 1
      : 1);
  }

  public get to(): string {
    const page = this.page;

    if (this.viewData.length === 0) {
      return String(0);
    }

    if (this.isTotalCountMorePerPage(page)) {
      return String(this.viewData.length);
    }

    if (this.isTotalCountLessPerPage(page)) {
      return String(this.viewData.length + (this.perPage * (page - 1)));
    }

    return String(page > 1
      ? page * this.perPage
      : this.perPage);
  }

  private totalPages = 1;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.perPageControl.valueChanges
      .pipe(
        skip(1),
        untilDestroyed(this),
      )
      .subscribe((perPage: number) => {
        this.paginatorData = {
          perPage,
          page: 1,
        };

        this.perPage = perPage;
        this.page = 1;

        this.changed$.emit(this.paginatorData);

        this.paginate(this.page, true);

        this.changeDetectorRef.detectChanges();
      });
  }

  public ngOnInit(): void {
    this.fixInputProps();

    this.perPageControl.setValue(this.perPage);

    this.paginatorData = {
      perPage: this.perPage,
      page: this.page,
    };

    this.paginate(this.page, true);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.perPage?.currentValue && !this.perPageList.includes(this.perPage)) {
      throw new Error('Вы передали не корректный perPage, его нет в списке perPageList');
    }

    if (this.isNeedPaginate(changes)) {
      this.fixInputProps();
      this.paginate(this.page, true);
    }
  }

  public nextPage(): void {
    this.paginate(this.page + 1);
  }

  public prevPage(): void {
    this.paginate(this.page - 1);
  }

  public isCurrentPage(page: number): boolean {
    return this.page === page;
  }

  public isFirstPage(): boolean {
    return this.page === 1 || this.page === 0;
  }

  public isLastPage(): boolean {
    return this.page === this.totalPages;
  }

  public fixInputProps(): void {
    this.limit = this.limit < 0 ? 0 : this.limit;
    this.perPage = this.perPage < 1 ? 1 : this.perPage;
    this.total = this.total === 0 || this.total < 0 ? 0 : this.total;

    /** если total=0 то должна отображаться хотя бы одна страница */
    if (this.total === 0) {
      this.pages.push(1);
    }
  }

  public paginate(pageNumber: number, skipEmit: boolean = false): void {
    this.totalPages = Math.ceil(this.total / this.perPage);

    if (pageNumber < 1) {
      pageNumber = 1;
    } else if (pageNumber > this.totalPages) {
      pageNumber = this.totalPages;
    }

    const extremePageIndexes = this.calculateFirstAndLastPageIndexes(pageNumber);

    this.pages = [];
    for (let i = extremePageIndexes.firstPage; i <= extremePageIndexes.lastPage; i++) {
      this.pages.push(i);
    }

    if (this.pages.length === 0) {
      this.pages.push(1);
      this.page = 1;
    }

    this.paginatorData.page = pageNumber;

    if (!skipEmit) {
      // todo: подумать как поднять этот if в начало функции (баг с пагинацией на одну и ту же страницу)
      if (this.page === pageNumber) {
        return;
      }
      this.changed$.emit(this.paginatorData);
    }

    this.page = pageNumber;
  }

  public getPerPageText(perPage: number): string {
    return perPage > 9000
      ? 'Все'
      : perPage.toString();
  }

  private calculateFirstAndLastPageIndexes(pageNumber: number): any {
    let firstPage: number;
    let lastPage: number;
    if (this.totalPages <= this.limit || this.limit === 0) {
      firstPage = 1;
      lastPage = this.totalPages;
    } else {
      const maxPagesBeforeCurrentPage = Math.floor(this.limit / 2);
      const maxPagesAfterCurrentPage = Math.ceil(this.limit / 2) - 1;

      if (pageNumber <= maxPagesBeforeCurrentPage) {
        firstPage = 1;
        lastPage = this.limit;
      } else if (pageNumber + maxPagesAfterCurrentPage >= this.totalPages) {
        firstPage = this.totalPages - this.limit + 1;
        lastPage = this.totalPages;
      } else {
        firstPage = pageNumber - maxPagesBeforeCurrentPage;
        lastPage = pageNumber + maxPagesAfterCurrentPage;
      }
    }

    return {
      firstPage,
      lastPage,
    };
  }

  private isNeedPaginate(changes: SimpleChanges): boolean {
    const isPerPage = changes.perPage && !changes.perPage.firstChange;
    const isTotal = changes.total && !changes.total.firstChange;
    const isLimit = changes.limit && !changes.limit.firstChange;
    const isPage = changes.page && !changes.page.firstChange;

    return isPerPage || isTotal || isLimit || isPage;
  }

  private isTotalCountMorePerPage(page: number): boolean {
    return this.total > this.perPage * page && this.viewData.length !== this.perPage;
  }

  private isTotalCountLessPerPage(page: number): boolean {
    return this.perPage * page > this.total && this.viewData.length !== this.perPage;
  }
}
